// ── programs-collection: useCollection client adapter for /programs ────────

import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  humanizeLabel,
  useCollection,
  type CollectionColumnDef,
  type CollectionFilterDef,
  type UseCollectionResult
} from "@vjal/ui";

import type { CoachingProgramDto } from "../../services/coaching-dtos";
import { readDirectoryInitialState } from "../directory-collection-state";
import { programStatusTone } from "./program-detail-helpers";
import { PROGRAM_FILTER_STATUSES } from "./programs-write";

export const PROGRAMS_PAGE_SIZE = 25;
export const PROGRAMS_DEFAULT_SORT = { id: "name", dir: "asc" as const };

export type ProgramRow = CoachingProgramDto & Record<string, unknown>;

export interface ProgramsCollectionActions {
  allowUpdate: boolean;
  onEdit: (row: CoachingProgramDto) => void;
  onPublish: (row: CoachingProgramDto) => void;
  onArchive: (row: CoachingProgramDto) => void;
}

function orDash(value: string | null | undefined): string {
  const trimmed = (value ?? "").trim();
  return trimmed.length > 0 ? trimmed : "—";
}

export function buildProgramsFilterDefs(): CollectionFilterDef<ProgramRow>[] {
  return [
    {
      id: "status",
      label: "Status",
      type: "select",
      placeholder: "All statuses",
      getValue: (row) => row.status ?? "draft",
      options: PROGRAM_FILTER_STATUSES.map((status) => ({
        value: status,
        label: humanizeLabel(status)
      }))
    }
  ];
}

export function buildProgramsCollectionColumns(
  actions: ProgramsCollectionActions
): CollectionColumnDef<ProgramRow>[] {
  const columns: CollectionColumnDef<ProgramRow>[] = [
    {
      id: "name",
      header: "Program",
      sortable: true,
      sortAccessor: (row) => row.name ?? "",
      searchValue: (row) => `${row.name ?? ""} ${row.company_name ?? ""} ${row.coach_name ?? ""} ${row.id}`,
      priority: 1,
      cell: (row) => (
        <Link
          to={`/programs/${encodeURIComponent(row.id)}`}
          className="block min-w-0 max-w-[14rem] truncate font-medium text-link no-underline hover:underline sm:max-w-[20rem]"
          title={row.name || "Unnamed program"}
          data-exact-set-name="Open program">
          {row.name || "Unnamed program"}
        </Link>
      )
    },
    {
      id: "company_name",
      header: "Company",
      sortable: true,
      sortAccessor: (row) => row.company_name ?? "",
      priority: 2,
      cell: (row) => <span className="text-ink-muted">{orDash(row.company_name)}</span>
    },
    {
      id: "coach_name",
      header: "Coach",
      sortable: true,
      sortAccessor: (row) => row.coach_name ?? "",
      priority: 2,
      cell: (row) => <span className="text-ink-muted">{orDash(row.coach_name)}</span>
    },
    {
      id: "status",
      header: "Status",
      sortable: true,
      sortAccessor: (row) => row.status ?? "draft",
      priority: 2,
      cell: (row) => <Badge variant={programStatusTone(row.status)}>{humanizeLabel(row.status ?? "draft")}</Badge>
    }
  ];

  if (actions.allowUpdate) {
    columns.push({
      id: "_actions",
      header: "",
      sortable: false,
      priority: 1,
      cell: (row) => (
        <div className="flex justify-end gap-2">
          {row.status === "draft" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => actions.onPublish(row)}
              aria-label={`Publish ${row.name}`}
              data-exact-set-name="Publish program">
              Publish
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => actions.onEdit(row)} aria-label={`Edit ${row.name}`} data-exact-set-name="Edit program">
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => actions.onArchive(row)}
            aria-label={`Archive ${row.name}`}
            disabled={row.status === "archived"}
            data-exact-set-name="Archive program">
            Archive
          </Button>
        </div>
      )
    });
  }

  return columns;
}

export function useProgramsCollection(
  rows: CoachingProgramDto[],
  actions: ProgramsCollectionActions,
  routePath?: string
): UseCollectionResult<ProgramRow> {
  const filters = useMemo(() => buildProgramsFilterDefs(), []);
  const columns = useMemo(() => buildProgramsCollectionColumns(actions), [actions]);
  const initialState = useMemo(
    () => ({ sort: PROGRAMS_DEFAULT_SORT, ...readDirectoryInitialState(routePath, PROGRAMS_PAGE_SIZE) }),
    [routePath]
  );

  return useCollection<ProgramRow>({
    mode: "client",
    rows: rows as ProgramRow[],
    columns,
    filters,
    pageSize: PROGRAMS_PAGE_SIZE,
    urlSync: "query",
    views: "coaching-programs",
    initialState,
    getRowKey: (row) => row.id
  });
}
